import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/forms/Buttons/Button";
import { toast } from "react-toastify";
import { postResetPasswordTeamMember } from "../../../services/olympiad/teamMemberService";
import CustomDialog from "../../../components/modals/CustomDialog/CustomDialog";
import Input from "../../../components/forms/Input/Input";

export default function MemberCard({
  member,
  onRemove,
  onEdit,
  canEdit = true,
  setTeam,
  isSupervisor,
}) {
  const [t] = useTranslation();
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState(
    member.user?.email || ""
  );

  useEffect(() => {
    if (isResetPasswordModalOpen) {
      setResetPasswordEmail(member.user?.email || "");
    }
  }, [isResetPasswordModalOpen, member.user?.email]);

  const handleRemove = () => {
    if (toast.isActive(member.uuid)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>{t("team.remove_member_confirmation")}</p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={() => {
              toast.dismiss(member.uuid);
              onRemove(member.uuid)
                .then((res) => {
                  toast.success(t("team.member_removed"));
                })
                .catch((err) => {
                  toast.error(t("team.error_removing_member"));
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(member.uuid)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: member.uuid }
    );
  };

  const handleResetPassword = () => {
    setIsResetPasswordModalOpen(true);
  };

  const handleResetPasswordSubmit = () => {
    if (!resetPasswordEmail) {
      toast.error(t("student.email") + " " + t("signup.required"));
      return;
    }

    postResetPasswordTeamMember(member.uuid, resetPasswordEmail)
      .then(() => {
        toast.success(t("team.password_will_be_sent_to_email"));
        setIsResetPasswordModalOpen(false);
        setResetPasswordEmail(member.user?.email || "");
        setTeam((prevTeam) => ({
          ...prevTeam,
          team_members: prevTeam.team_members.map((m) =>
            m.uuid === member.uuid
              ? { ...m, user: { ...m.user, email: resetPasswordEmail } }
              : m
          ),
        }));
      })
      .catch((err) => {
        if (err.response.data.code === "EMAIL_ALREADY_EXISTS") {
          toast.error(t("signup.email_is_taken"));
        } else {
          toast.error(err.response.data.detail);
        }
      });
  };

  const handleCloseResetPasswordModal = () => {
    setIsResetPasswordModalOpen(false);
    setResetPasswordEmail(member.user?.email || "");
  };

  const isPublishedFirstLevel = member?.user?.user_results?.find(
    (result) => result?.variant?.level === 0
  )?.is_published;

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
      <div className="flex-1">
        <p className="font-medium">
          {member.user?.first_name} {member.user?.last_name}{" "}
          {member.user?.middle_name}
        </p>
        <p className="text-sm text-gray-600">{member.email}</p>
        <p className="text-sm text-gray-500">
          {t("auth.iin")}: {member.user?.username}
        </p>
        <p className="text-sm text-gray-500">
          {t("student.email")}: {member.user?.email}
        </p>
        <p className="text-sm text-gray-500">
          {t("signup.phone_number")}: {member.user?.phone_number}
        </p>
        {/* <p className="text-sm text-gray-500">
          {t("signup.study_lang")}:{" "}
          {member.user?.language === "K"
            ? "Қазақша"
            : member.user?.language === "R"
            ? "Русский"
            : member.user?.language}
        </p> */}
        <p className="text-sm text-gray-500">
          {t("signup.gender")}:{" "}
          {member.user?.gender === "M" ? t("signup.male") : t("signup.female")}
        </p>
        <p className="text-sm text-gray-500">
          {t("team.grade_option", {
            value: member.user?.grade,
          })}
        </p>
        <p className="text-sm text-gray-500">
          {t("team.room")}: {member?.seat?.classroom?.room_number}
        </p>
        <p className="text-sm text-gray-500">
          {t("team.seat")}: {member?.seat?.seat_number}
        </p>
        {isPublishedFirstLevel && (
          <p className="font-bold text-sm text-primary">
            {t("team.member_score")}:{" "}
            {
              member?.user?.user_results?.find(
                (result) => result?.variant?.level === 0
              )?.result
            }
          </p>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-2 ml-4">
        {canEdit && (
          <Button
            text={t("buttons.edit")}
            color="primary"
            onClick={() => onEdit(member)}
            className=""
          />
        )}
        {isSupervisor && (
          <Button
            text={t("signup.reset_password")}
            color="secondary"
            onClick={handleResetPassword}
            className="h-full min-h-10"
          />
        )}
        {canEdit && (
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={handleRemove}
            className=""
          />
        )}
      </div>

      <CustomDialog
        isOpen={isResetPasswordModalOpen}
        headerText={t("signup.reset_password")}
        closeModal={handleCloseResetPasswordModal}
      >
        <div className="flex flex-col gap-4">
          <Input
            label={t("student.email")}
            type="email"
            value={resetPasswordEmail}
            onChange={(e) => setResetPasswordEmail(e.target.value)}
            required
          />
          <div className="flex justify-end gap-2">
            <Button
              text={t("buttons.cancel")}
              onClick={handleCloseResetPasswordModal}
              color="secondary"
            />
            <Button
              text={t("buttons.save")}
              onClick={handleResetPasswordSubmit}
              color="primary"
            />
          </div>
        </div>
      </CustomDialog>
    </div>
  );
}
